import {useMemo} from "react";
import {create} from "zustand";

import {
  buildBlockingGateQueue,
  selectActiveGateId,
  type BlockingGateDescriptor,
  type BlockingGateId,
} from "@/src/modules/core/lib/compliance-gate-priority";

export type ReconcileOwner = BlockingGateId | "system";

type BlockingGateState = {
  gates: Partial<Record<BlockingGateId, BlockingGateDescriptor>>;
  /** True while ANY reconcile beat is in flight (refcounted). */
  reconciling: boolean;
  /** Number of overlapping in-flight reconcile beats; `reconciling === reconcileCount > 0`. */
  reconcileCount: number;
  /** Per-owner reconcile refcounts for overlay scoping. */
  reconcileOwnerCounts: Partial<Record<ReconcileOwner, number>>;
};

type BlockingGateActions = {
  registerGate: (descriptor: BlockingGateDescriptor) => void;
  clearGate: (id: BlockingGateId) => void;
  /** Refcounted reconcile beat; pass the same `owner` for true/false pairs. */
  setReconciling: (value: boolean, owner: ReconcileOwner) => void;
};

function isSameDescriptor(a: BlockingGateDescriptor | undefined, b: BlockingGateDescriptor) {
  return (
    a != null &&
    a.id === b.id &&
    a.priority === b.priority &&
    a.label === b.label &&
    a.deferrable === b.deferrable
  );
}

function adjustOwnerCount(
  counts: Partial<Record<ReconcileOwner, number>>,
  owner: ReconcileOwner,
  delta: 1 | -1,
): Partial<Record<ReconcileOwner, number>> {
  const next = {...counts};
  const current = next[owner] ?? 0;
  const updated = Math.max(0, current + delta);
  if (updated === 0) {
    delete next[owner];
  } else {
    next[owner] = updated;
  }
  return next;
}

export const useBlockingGateStore = create<BlockingGateState & BlockingGateActions>((set) => ({
  gates: {},
  reconciling: false,
  reconcileCount: 0,
  reconcileOwnerCounts: {},

  registerGate: (descriptor) =>
    set((state) => {
      if (isSameDescriptor(state.gates[descriptor.id], descriptor)) {
        return state;
      }
      return {gates: {...state.gates, [descriptor.id]: descriptor}};
    }),

  clearGate: (id) =>
    set((state) => {
      if (state.gates[id] == null) {
        return state;
      }
      const next = {...state.gates};
      delete next[id];
      return {gates: next};
    }),

  setReconciling: (value, owner) =>
    set((state) => {
      const reconcileCount = Math.max(0, state.reconcileCount + (value ? 1 : -1));
      const reconciling = reconcileCount > 0;
      const reconcileOwnerCounts = adjustOwnerCount(
        state.reconcileOwnerCounts,
        owner,
        value ? 1 : -1,
      );
      if (
        reconcileCount === state.reconcileCount &&
        reconciling === state.reconciling &&
        reconcileOwnerCounts === state.reconcileOwnerCounts
      ) {
        return state;
      }
      return {reconcileCount, reconciling, reconcileOwnerCounts};
    }),
}));

/** Id of the single gate that should be visible, by priority. */
export function useActiveBlockingGateId(): BlockingGateId | null {
  return useBlockingGateStore((state) => selectActiveGateId(state.gates));
}

/** Priority-sorted descriptors, for the sticky bar. */
export function useBlockingGateQueue(): BlockingGateDescriptor[] {
  const gates = useBlockingGateStore((state) => state.gates);
  return useMemo(() => buildBlockingGateQueue(gates), [gates]);
}

export function useBlockingGateReconciling(): boolean {
  return useBlockingGateStore((state) => state.reconciling);
}

export function useReconcileOwnerCounts(): Partial<Record<ReconcileOwner, number>> {
  return useBlockingGateStore((state) => state.reconcileOwnerCounts);
}

export function useRegisterBlockingGate() {
  return useBlockingGateStore((state) => state.registerGate);
}

export function useClearBlockingGate() {
  return useBlockingGateStore((state) => state.clearGate);
}

export function useSetBlockingGateReconciling() {
  return useBlockingGateStore((state) => state.setReconciling);
}
